import React, { useState, useMemo, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "tailwindcss/tailwind.css";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { BASE_URL } from "../../config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding, faPlus } from "@fortawesome/free-solid-svg-icons";
import {
  MagnifyingGlassIcon,
  EyeIcon,
  PencilSquareIcon,
  ClockIcon,
  DocumentCheckIcon,
  PlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  Cog6ToothIcon,
  CurrencyDollarIcon,
  EllipsisVerticalIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  ArrowTopRightOnSquareIcon,
  PaperClipIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import { useTable, usePagination } from "react-table";
import clsx from "clsx";
import CorporateViewModal from "./CorporateViewModal";

const Badge = ({ children, color = "gray" }) => {
  const colors = {
    gray: "bg-gray-100 text-gray-700 ring-gray-200",
    green: "bg-emerald-100 text-emerald-700 ring-emerald-200",
    blue: "bg-blue-100 text-blue-700 ring-blue-200",
    red: "bg-rose-100 text-rose-700 ring-rose-200",
    yellow: "bg-amber-100 text-amber-700 ring-amber-200",
    slate: "bg-slate-100 text-slate-700 ring-slate-200",
  };
  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1",
        colors[color]
      )}
    >
      {children}
    </span>
  );
};

const EmptyState = ({
  title = "No results",
  subtitle = "Try adjusting your search or filters.",
}) => (
  <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 p-10 text-center">
    <svg
      viewBox="0 0 24 24"
      className="mb-2 h-8 w-8 text-gray-400"
      fill="none"
      stroke="currentColor"
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
    <div className="text-sm font-medium text-gray-900">{title}</div>
    <div className="text-xs text-gray-500">{subtitle}</div>
  </div>
);

const formatCurrency = (value) => {
  if (value == null || value === "") return "N/A";
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return value;
  return new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    maximumFractionDigits: 2,
  }).format(parsed);
};

const formatFundManagementFlag = (value) => {
  if (value == null || value === "") return "N/A";
  return Number(value) === 1 ? "Enabled" : "Disabled";
};

const ATTACHMENT_TOO_LARGE_MESSAGE =
  "The attached file or request data is too large. Please reduce the file size and try again.";

const getReadableApiMessage = (message) => {
  if (!message) return "";
  if (typeof message === "string") return message;
  if (Array.isArray(message)) return getReadableApiMessage(message[0]);
  if (typeof message === "object") {
    const firstKey = Object.keys(message)[0];
    return firstKey ? getReadableApiMessage(message[firstKey]) : "";
  }
  return String(message);
};

const formatApiErrorMessage = (error) => {
  const status = error?.response?.status;
  const responseData = error?.response?.data;

  if (status === 413) {
    return ATTACHMENT_TOO_LARGE_MESSAGE;
  }

  if (responseData) {
    const responseMessage = getReadableApiMessage(
      responseData?.message || responseData
    );

    if (
      status === 404 &&
      /route .*could not be found/i.test(responseMessage)
    ) {
      return "The requested endpoint was not found. Please refresh the page and try again.";
    }

    if (responseMessage) return responseMessage;
  }

  return error?.message || "Unknown error";
};

const formatChangeStatusErrorMessage = (error, hasAttachedFiles = false) => {
  if (
    hasAttachedFiles &&
    !error?.response &&
    String(error?.message || "").toLowerCase() === "network error"
  ) {
    return ATTACHMENT_TOO_LARGE_MESSAGE;
  }

  return formatApiErrorMessage(error);
};

const formatFundUtilPercent = (revolvingFund, remainingFund) => {
  const revolving = Number(revolvingFund);
  const remaining = Number(remainingFund);

  if (
    !Number.isFinite(revolving) ||
    revolving === 0 ||
    !Number.isFinite(remaining)
  ) {
    return "N/A";
  }

  return `${(((revolving - remaining) / revolving) * 100).toFixed(2)}%`;
};

const formatMemberStatusSummaryText = (corporate) => {
  const total = Number(corporate?.total_member_count ?? 0);
  const normalizedTotal = Number.isFinite(total) ? total : 0;
  const totalLabel = `${normalizedTotal.toLocaleString()} total member${
    normalizedTotal === 1 ? "" : "s"
  }`;

  if (Array.isArray(corporate?.member_status_counts)) {
    const statusSummary = corporate.member_status_counts
      .filter((status) => Number(status?.count ?? 0) > 0)
      .map((status) => {
        const count = Number(status.count || 0);
        const label = String(status.status_label || "Unknown").trim();
        return `${count.toLocaleString()} ${label || "Unknown"}`;
      })
      .join(", ");

    return statusSummary ? `${totalLabel}\n${statusSummary}` : `${totalLabel}\nNo members`;
  }

  const summary = String(corporate?.member_status_summary || "").trim();
  return summary ? `${totalLabel}\n${summary}` : `${totalLabel}\nNo members`;
};

const StatCard = ({ icon: Icon, label, value, accent = "blue" }) => {
  const accents = {
    blue: "bg-blue-50 text-blue-600",
    emerald: "bg-emerald-50 text-emerald-600",
    violet: "bg-violet-50 text-violet-600",
    amber: "bg-amber-50 text-amber-600",
  };

  return (
    <div className="rounded-lg border border-gray-100 bg-white p-3 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={clsx("rounded-lg p-2", accents[accent])}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-xs text-gray-500">{label}</div>
          <div className="truncate text-sm font-semibold text-gray-900">
            {value ?? "N/A"}
          </div>
        </div>
      </div>
    </div>
  );
};

const movementUpdateForMap = {
  "0": { label: "Inactive", color: "red" },
  "1": { label: "Active/Reactivated", color: "green" },
  "2": { label: "Terminated/Cancelled", color: "red" },
  "3": { label: "Suspended", color: "yellow" },
  "4": { label: "Expired", color: "slate" },
  "5": { label: "Extended", color: "blue" },
};

const getMovementUpdateForMeta = (value) => {
  const normalized = String(value ?? "").trim();
  return movementUpdateForMap[normalized] || {
    label: value || "N/A",
    color: "gray",
  };
};

const formatMovementDate = (value, includeTime = false) => {
  const raw = String(value ?? "").trim();
  if (!raw || raw.startsWith("0000-00-00")) return "N/A";

  const parsed = new Date(raw.includes("T") ? raw : raw.replace(" ", "T"));
  if (Number.isNaN(parsed.getTime())) return raw;

  return includeTime
    ? parsed.toLocaleString("en-PH", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    : parsed.toLocaleDateString("en-PH", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });
};

const formatAttachmentFileSize = (bytes) => {
  const size = Number(bytes || 0);
  if (!Number.isFinite(size) || size <= 0) return "";

  const units = ["B", "KB", "MB", "GB"];
  let value = size;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return `${value.toFixed(unitIndex === 0 ? 0 : 2)} ${units[unitIndex]}`;
};

const isAttachmentUrlExpired = (urlExpiresAt) => {
  if (!urlExpiresAt) return false;
  const timestamp = new Date(urlExpiresAt).getTime();
  if (Number.isNaN(timestamp)) return false;
  return timestamp <= Date.now();
};

const getHistoryAttachmentFileName = (attachment) =>
  attachment?.original_file_name || attachment?.file_name || "Attachment";

const MOVEMENT_HISTORY_EXPORT_COLUMNS = [
  { key: "update_for", label: "Update For" },
  { key: "effective_date", label: "Effective Date" },
  { key: "updated_by", label: "Updated By" },
  { key: "reason", label: "Reason" },
  { key: "remarks", label: "Remarks" },
  { key: "lastupdate_date", label: "Last Update" },
  { key: "attachments", label: "Attachments" },
];

const HISTORY_FILTER_DEFAULTS = Object.freeze({
  search_string: "",
  update_for: "all",
  updated_by: "all",
  effective_date_from: "",
  effective_date_to: "",
  lastupdate_date_from: "",
  lastupdate_date_to: "",
});

const exportStamp = () => {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, "0");
  return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(
    now.getDate()
  )}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
};

const toMovementHistoryExportCellValue = (row, key) => {
  switch (key) {
    case "update_for":
      return getMovementUpdateForMeta(row.update_for).label;
    case "effective_date":
      return formatMovementDate(row.effective_date);
    case "lastupdate_date":
      return formatMovementDate(row.lastupdate_date, true);
    case "attachments": {
      const attachments = Array.isArray(row.attachments) ? row.attachments : [];
      return attachments.map(getHistoryAttachmentFileName).join("; ");
    }
    default:
      return row[key] ?? "";
  }
};

const exportMovementHistoryToCsv = (rows, fileName) => {
  const escapeCsvValue = (value) => {
    if (value == null) return "";
    const raw = String(value);
    if (/["\n,]/.test(raw)) return `"${raw.replace(/"/g, '""')}"`;
    return raw;
  };

  const headerLine = MOVEMENT_HISTORY_EXPORT_COLUMNS.map((column) =>
    escapeCsvValue(column.label)
  ).join(",");
  const rowLines = rows.map((row) =>
    MOVEMENT_HISTORY_EXPORT_COLUMNS.map((column) =>
      escapeCsvValue(toMovementHistoryExportCellValue(row, column.key))
    ).join(",")
  );

  const blob = new Blob([[headerLine, ...rowLines].join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const exportMovementHistoryToExcel = async (rows, fileName) => {
  const XLSX = await import("xlsx");
  const exportRows = rows.map((row) =>
    MOVEMENT_HISTORY_EXPORT_COLUMNS.reduce((acc, column) => {
      acc[column.label] = toMovementHistoryExportCellValue(row, column.key);
      return acc;
    }, {})
  );
  const worksheet = XLSX.utils.json_to_sheet(exportRows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Corporate Movement");
  XLSX.writeFile(workbook, fileName);
};

const ACTION_MENU_WIDTH = 288;
const ACTION_MENU_HEIGHT = 300;
const ACTION_MENU_MARGIN = 12;
const ACTION_MENU_GAP = 8;

const getActionMenuPosition = (triggerElement) => {
  const rect = triggerElement.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let left = rect.right - ACTION_MENU_WIDTH;
  left = Math.max(
    ACTION_MENU_MARGIN,
    Math.min(left, viewportWidth - ACTION_MENU_WIDTH - ACTION_MENU_MARGIN)
  );

  let top = rect.bottom + ACTION_MENU_GAP;
  let placement = "bottom";

  if (top + ACTION_MENU_HEIGHT > viewportHeight - ACTION_MENU_MARGIN) {
    top = rect.top - ACTION_MENU_HEIGHT - ACTION_MENU_GAP;
    placement = "top";
  }

  const maxTop = Math.max(
    ACTION_MENU_MARGIN,
    viewportHeight - ACTION_MENU_HEIGHT - ACTION_MENU_MARGIN
  );
  top = Math.max(ACTION_MENU_MARGIN, Math.min(top, maxTop));

  return { top, left, placement };
};

const getCorporateActionKey = (corp) => corp?.corp_code || corp?.tbl_id || "";

const CorporateTable = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("corp_code");
  const [sortDirection, setSortDirection] = useState("asc");

  const [statusFilter, setStatusFilter] = useState("ALL");
  const [corpPrefixFilter, setCorpPrefixFilter] = useState("ALL");
  const [mainSubFilter, setMainSubFilter] = useState("ALL");
  const [corpPrefixes, setCorpPrefixes] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCorp, setSelectedCorp] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [reason, setReason] = useState("");
  const [files, setFiles] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [viewCorpCode, setViewCorpCode] = useState(null);

  const [finalizeModalOpen, setFinalizeModalOpen] = useState(false);
  const [finalizeCorpCode, setFinalizeCorpCode] = useState(null);
  const [finalizeCorpName, setFinalizeCorpName] = useState("");
  const [finalizePassword, setFinalizePassword] = useState("");
  const [finalizeLoading, setFinalizeLoading] = useState(false);

  const [addFundModalOpen, setAddFundModalOpen] = useState(false);
  const [addFundCorpCode, setAddFundCorpCode] = useState(null);
  const [addFundCorpName, setAddFundCorpName] = useState("");
  const [addFundAmount, setAddFundAmount] = useState("");
  const [addFundBankRef, setAddFundBankRef] = useState("");
  const [addFundRemarks, setAddFundRemarks] = useState("");
  const [addFundLoading, setAddFundLoading] = useState(false);
  const [addFundInformation, setAddFundInformation] = useState(null);
  const [addFundInfoLoading, setAddFundInfoLoading] = useState(false);
  const [addFundMaximized, setAddFundMaximized] = useState(false);
  const [fundHistoryExportingCsv, setFundHistoryExportingCsv] = useState(false);
  const [fundHistoryExportingExcel, setFundHistoryExportingExcel] =
    useState(false);
  const [fundHistory, setFundHistory] = useState([]);
  const [fundHistoryPage, setFundHistoryPage] = useState(1);
  const [fundHistoryPerPage, setFundHistoryPerPage] = useState(10);
  const [fundHistoryTotal, setFundHistoryTotal] = useState(0);
  const [fundHistorySearch, setFundHistorySearch] = useState("");
  const [remainingFund, setRemainingFund] = useState(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [fundContacts, setFundContacts] = useState([]);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyTotalCount, setHistoryTotalCount] = useState(0);
  const [historyCurrentPage, setHistoryCurrentPage] = useState(1);
  const [historyItemsPerPage, setHistoryItemsPerPage] = useState(10);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyCorp, setHistoryCorp] = useState(null);
  const [historyFilters, setHistoryFilters] = useState({
    ...HISTORY_FILTER_DEFAULTS,
  });
  const [historyAppliedFilters, setHistoryAppliedFilters] = useState({
    ...HISTORY_FILTER_DEFAULTS,
  });
  const [historySortBy, setHistorySortBy] = useState("lastupdate_date");
  const [historySortDirection, setHistorySortDirection] = useState("desc");
  const [historyUpdateForOptions, setHistoryUpdateForOptions] = useState([]);
  const [historyUpdatedByOptions, setHistoryUpdatedByOptions] = useState([]);
  const [historyExportingCsv, setHistoryExportingCsv] = useState(false);
  const [historyExportingExcel, setHistoryExportingExcel] = useState(false);
  const [historyRefreshingAttachmentId, setHistoryRefreshingAttachmentId] =
    useState(null);

  const statusMap = {
    0: { name: "Inactive", color: "red" },
    1: { name: "Active/Reactivated", color: "green" },
    2: { name: "Terminated/Cancelled", color: "red" },
    3: { name: "Suspended", color: "yellow" },
    4: { name: "Expired", color: "slate" },
    5: { name: "Extended", color: "blue" },
    default: { name: "Unknown", color: "gray" },
  };

  const finalizeStatusMap = {
    finalized: { name: "Finalized", color: "green" },
    pending: { name: "Pending", color: "yellow" },
    default: { name: "Unknown", color: "gray" },
  };
  const mainSubStatusMap = {
    main: { name: "Main Corp", color: "blue" },
    sub: { name: "Sub Corp", color: "yellow" },
    default: { name: "Unknown", color: "gray" },
  };

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
  });

  const fetchCorporates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/corporates`, {
        headers: getAuthHeaders(),
        params: {
          limit_offset: (currentPage - 1) * itemsPerPage,
          search_string: searchQuery,
          sort_by: sortBy,
          sort_direction: sortDirection,
          limit: itemsPerPage,
          status_flag: statusFilter === "ALL" ? undefined : statusFilter,
          corp_prefix: corpPrefixFilter === "ALL" ? undefined : corpPrefixFilter,
          main_sub: mainSubFilter === "ALL" ? undefined : mainSubFilter,
        },
      });
      setData(response.data.corporates || []);
      setTotalCount(response.data.total_count || 0);
    } catch (error) {
      toast.error("Failed to fetch corporates.", { autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    itemsPerPage,
    searchQuery,
    sortBy,
    sortDirection,
    statusFilter,
    corpPrefixFilter,
    mainSubFilter,
  ]);

  const fetchCorpPrefixes = useCallback(async () => {
    try {
      const response = await axios.get(`${BASE_URL}/corporates/corp-prefixes`, {
        headers: getAuthHeaders(),
      });
      setCorpPrefixes(response.data.data || []);
    } catch (error) {
      toast.error("Failed to fetch corp prefixes.", { autoClose: 3000 });
    }
  }, []);

  const fetchFundHistory = useCallback(async (corpCode) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/corporates/${corpCode}/fund-history`,
        {
          headers: getAuthHeaders(),
          params: {
            limit_offset: (fundHistoryPage - 1) * fundHistoryPerPage,
            limit: fundHistoryPerPage,
            search_string: fundHistorySearch,
          },
        }
      );
      setFundHistory(response.data.data || []);
      setFundHistoryTotal(response.data.total_count || 0);
      setRemainingFund(response.data.remaining_fund || 0);
      setFundContacts(response.data.fund_contacts || []);
    } catch (error) {
      toast.error("Failed to fetch fund history.", { autoClose: 3000 });
    }
  }, [fundHistoryPage, fundHistoryPerPage, fundHistorySearch]);

  const fetchAddFundInformation = useCallback(async (corpCode) => {
    if (!corpCode) return;

    setAddFundInfoLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/corporates/${corpCode}/funds`,
        { headers: getAuthHeaders() }
      );
      const fundData = response.data.data || null;
      setAddFundInformation(fundData);
      setRemainingFund(
        fundData?.remaining_fund ?? fundData?.remaining_fund_amt ?? 0
      );
      if (Array.isArray(fundData?.fund_contacts)) {
        setFundContacts(fundData.fund_contacts);
      }
    } catch (error) {
      setAddFundInformation(null);
      toast.error("Failed to fetch fund information.", { autoClose: 3000 });
    } finally {
      setAddFundInfoLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCorporates();
    fetchCorpPrefixes();
  }, [fetchCorporates, fetchCorpPrefixes]);

  useEffect(() => {
    if (addFundCorpCode && addFundModalOpen) {
      fetchFundHistory(addFundCorpCode);
    }
  }, [addFundCorpCode, addFundModalOpen, fetchFundHistory]);

  useEffect(() => {
    if (addFundCorpCode && addFundModalOpen) {
      fetchAddFundInformation(addFundCorpCode);
    }
  }, [addFundCorpCode, addFundModalOpen, fetchAddFundInformation]);

  const openModal = (corp) => {
    setSelectedCorp(corp);
    setNewStatus("");
    setReason("");
    setFiles([]);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedCorp(null);
    setNewStatus("");
    setReason("");
    setFiles([]);
    setModalLoading(false);
  };

  const openAddFundModal = (corp) => {
    setAddFundCorpCode(corp.corp_code);
    setAddFundCorpName(corp.corp_name);
    setAddFundAmount("");
    setAddFundBankRef("");
    setAddFundRemarks("");
    setFundHistoryPage(1);
    setFundHistorySearch("");
    setAddFundInformation(null);
    setAddFundInfoLoading(true);
    setAddFundMaximized(false);
    setAddFundModalOpen(true);
  };

  const closeAddFundModal = () => {
    setAddFundModalOpen(false);
    setAddFundCorpCode(null);
    setAddFundCorpName("");
    setAddFundAmount("");
    setAddFundBankRef("");
    setAddFundRemarks("");
    setFundHistory([]);
    setFundHistoryTotal(0);
    setAddFundInformation(null);
    setAddFundInfoLoading(false);
    setAddFundMaximized(false);
    setRemainingFund(0);
    setFundContacts([]);
    setShowConfirmModal(false);
    setAddFundLoading(false);
    setFundHistoryExportingCsv(false);
    setFundHistoryExportingExcel(false);
  };

  const closeActionMenu = useCallback(() => {
    setOpenActionMenu(null);
    setActionMenuPosition(null);
  }, []);

  const handleActionMenuToggle = useCallback(
    (corp, event) => {
      const rowKey = getCorporateActionKey(corp);
      if (!rowKey) return;

      if (openActionMenu === rowKey) {
        closeActionMenu();
        return;
      }

      setOpenActionMenu(rowKey);
      setActionMenuPosition(getActionMenuPosition(event.currentTarget));
    },
    [closeActionMenu, openActionMenu]
  );

  useEffect(() => {
    const onEsc = (e) => {
      if (e.key === "Escape") {
        if (showConfirmModal) {
          setShowConfirmModal(false);
        } else if (isModalOpen) {
          closeModal();
        } else if (addFundModalOpen) {
          closeAddFundModal();
        }
      }
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [isModalOpen, addFundModalOpen, showConfirmModal]);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files).slice(0, 5);
    setFiles(selectedFiles);
  };

  const handleChangeStatus = async (e) => {
    e.preventDefault();
    if (!reason.trim()) {
      toast.error("Reason is required.");
      return;
    }
    if (!newStatus) {
      toast.error("Please select a new status.");
      return;
    }

    setModalLoading(true);
    try {
      const movementFormData = new FormData();
      movementFormData.append("corporate_code", selectedCorp.corp_code);
      movementFormData.append("corporate_flag", newStatus);
      movementFormData.append(
        "effectivity_date",
        new Date().toISOString().split("T")[0]
      );
      movementFormData.append("reason", reason);
      movementFormData.append("remarks", reason);
      movementFormData.append("status", "1");
      movementFormData.append("lastupdate_date", new Date().toISOString());
      movementFormData.append("update_by", "Admin");
      files.forEach((file) => movementFormData.append("files[]", file));

      await axios.post(
        `${BASE_URL}/corporate-movements`,
        movementFormData,
        { headers: getAuthHeaders() }
      );

      toast.success("Status updated successfully.");
      closeModal();
      fetchCorporates();
    } catch (error) {
      const errorMessage = formatChangeStatusErrorMessage(
        error,
        files.length > 0
      );
      const shouldShowDirectError =
        errorMessage === ATTACHMENT_TOO_LARGE_MESSAGE ||
        errorMessage.startsWith("Failed to upload file:") ||
        errorMessage.includes("endpoint was not found");

      toast.error(
        shouldShowDirectError
          ? errorMessage
          : `Failed to update status: ${errorMessage}`
      );
    } finally {
      setModalLoading(false);
    }
  };

  const handleAddFund = async () => {
    if (!addFundAmount || isNaN(addFundAmount) || addFundAmount <= 0) {
      toast.error("Valid amount is required.");
      return;
    }
    if (!addFundBankRef.trim()) {
      toast.error("Bank Reference # is required.");
      return;
    }
    if (!addFundRemarks.trim()) {
      toast.error("Remarks are required.");
      return;
    }

    setShowConfirmModal(true);
  };

  const confirmAddFund = async () => {
    setAddFundLoading(true);
    try {
      await axios.post(
        `${BASE_URL}/corporates/${addFundCorpCode}/add-fund`,
        {
          amount: parseFloat(addFundAmount).toFixed(2),
          bank_ref: addFundBankRef,
          remarks: addFundRemarks,
          update_by: "Admin", // Replace with actual user if available
        },
        { headers: getAuthHeaders() }
      );
      toast.success("Fund added successfully.");
      setShowConfirmModal(false);
      fetchFundHistory(addFundCorpCode);
      fetchAddFundInformation(addFundCorpCode);
      fetchCorporates();
      setAddFundAmount("");
      setAddFundBankRef("");
      setAddFundRemarks("");
    } catch (error) {
      toast.error(
        "Failed to add fund: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setAddFundLoading(false);
    }
  };

  const handleExportFundHistory = async (format = "csv") => {
    const normalizedFormat = format === "xlsx" ? "xlsx" : "csv";
    const setExporting =
      normalizedFormat === "xlsx"
        ? setFundHistoryExportingExcel
        : setFundHistoryExportingCsv;

    setExporting(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/corporates/${addFundCorpCode}/fund-history/export`,
        {
          headers: getAuthHeaders(),
          params: {
            search_string: fundHistorySearch,
            format: normalizedFormat,
          },
          responseType: "blob",
        }
      );

      const contentDisposition = response.headers?.["content-disposition"] || "";
      const fileNameMatch = contentDisposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
      );
      const fileName = fileNameMatch
        ? fileNameMatch[1].replace(/['"]/g, "")
        : `fund_history_${addFundCorpCode}_${exportStamp()}.${normalizedFormat}`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to export fund history.", { autoClose: 3000 });
    } finally {
      setExporting(false);
    }
  };

  const buildHistoryRequestParams = useCallback(
    ({ forExport = false } = {}) => {
      const effectiveFilters = historyAppliedFilters || HISTORY_FILTER_DEFAULTS;
      return {
        limit: forExport ? 10000 : historyItemsPerPage,
        limit_offset: forExport
          ? 0
          : Math.max(0, (historyCurrentPage - 1) * historyItemsPerPage),
        search_string: effectiveFilters.search_string || undefined,
        update_for:
          effectiveFilters.update_for && effectiveFilters.update_for !== "all"
            ? effectiveFilters.update_for
            : undefined,
        updated_by:
          effectiveFilters.updated_by && effectiveFilters.updated_by !== "all"
            ? effectiveFilters.updated_by
            : undefined,
        effective_date_from: effectiveFilters.effective_date_from || undefined,
        effective_date_to: effectiveFilters.effective_date_to || undefined,
        lastupdate_date_from:
          effectiveFilters.lastupdate_date_from || undefined,
        lastupdate_date_to: effectiveFilters.lastupdate_date_to || undefined,
        sort_by: historySortBy,
        sort_direction: historySortDirection,
        for_export: forExport ? "1" : "0",
      };
    },
    [
      historyAppliedFilters,
      historyCurrentPage,
      historyItemsPerPage,
      historySortBy,
      historySortDirection,
    ]
  );

  const loadCorporateMovementHistory = useCallback(
    async (corpCode, params, { silent = false } = {}) => {
      if (!corpCode) return null;

      if (!silent) setHistoryLoading(true);
      setHistoryError("");

      try {
        const response = await axios.get(
          `${BASE_URL}/corporates/${corpCode}/movement-history`,
          {
            headers: getAuthHeaders(),
            params,
          }
        );

        if (!silent) {
          setHistoryRows(response.data?.data || []);
          setHistoryTotalCount(Number(response.data?.total_count || 0));
          setHistoryUpdateForOptions(
            response.data?.filter_options?.update_for || []
          );
          setHistoryUpdatedByOptions(
            response.data?.filter_options?.updated_by || []
          );
        }

        return response.data?.data || [];
      } catch (error) {
        if (!silent) {
          const backendMessage = error.response?.data?.message;
          const message =
            (Array.isArray(backendMessage)
              ? backendMessage[0]
              : backendMessage) || "Failed to fetch corporate movement history.";
          setHistoryRows([]);
          setHistoryTotalCount(0);
          setHistoryError(message);
          toast.error(message, { autoClose: 3000 });
        }
        return null;
      } finally {
        if (!silent) setHistoryLoading(false);
      }
    },
    []
  );

  const handleRefreshHistoryAttachment = useCallback(
    async (attachment, { openAfterRefresh = false } = {}) => {
      const uploadId = attachment?.upload_id;
      if (!uploadId) {
        toast.error("Attachment reference is missing.");
        return null;
      }

      setHistoryRefreshingAttachmentId(uploadId);
      try {
        const response = await axios.post(
          `${BASE_URL}/corporates/document/regenerate-url`,
          { upload_id: uploadId },
          { headers: getAuthHeaders() }
        );
        const refreshed = response.data || {};
        const refreshedUrl =
          refreshed.public_share_url ||
          refreshed.signed_url ||
          attachment.signed_url;
        if (!refreshedUrl) {
          throw new Error("No refreshed attachment link was returned.");
        }

        setHistoryRows((prevRows) =>
          prevRows.map((row) => ({
            ...row,
            attachments: Array.isArray(row.attachments)
              ? row.attachments.map((item) =>
                  item.upload_id === uploadId
                    ? {
                        ...item,
                        signed_url: refreshedUrl,
                        url_expires_at:
                          refreshed.url_expires_at || item.url_expires_at,
                        is_url_expired: false,
                      }
                    : item
                )
              : row.attachments,
          }))
        );

        toast.success("Attachment link refreshed.");
        if (openAfterRefresh && refreshedUrl) {
          window.open(refreshedUrl, "_blank", "noopener,noreferrer");
        }

        return refreshedUrl;
      } catch (error) {
        toast.error(
          `Failed to refresh attachment link: ${formatApiErrorMessage(error)}`
        );
        return null;
      } finally {
        setHistoryRefreshingAttachmentId(null);
      }
    },
    []
  );

  const handleOpenHistoryAttachment = useCallback(
    async (attachment) => {
      if (!attachment) return;

      const expired =
        Boolean(attachment.is_url_expired) ||
        isAttachmentUrlExpired(attachment.url_expires_at);

      if (!attachment.signed_url || expired) {
        await handleRefreshHistoryAttachment(attachment, {
          openAfterRefresh: true,
        });
        return;
      }

      window.open(attachment.signed_url, "_blank", "noopener,noreferrer");
    },
    [handleRefreshHistoryAttachment]
  );

  const openCorporateHistoryModal = useCallback((corp) => {
    const corpCode = corp?.corp_code;
    if (!corpCode) return;

    setHistoryCorp({
      corp_code: corpCode,
      corp_name: corp.corp_name || corp.actual_corp_name || corpCode,
    });
    setHistoryFilters({ ...HISTORY_FILTER_DEFAULTS });
    setHistoryAppliedFilters({ ...HISTORY_FILTER_DEFAULTS });
    setHistorySortBy("lastupdate_date");
    setHistorySortDirection("desc");
    setHistoryItemsPerPage(10);
    setHistoryCurrentPage(1);
    setHistoryRows([]);
    setHistoryTotalCount(0);
    setHistoryError("");
    setHistoryUpdateForOptions([]);
    setHistoryUpdatedByOptions([]);
    setIsHistoryModalOpen(true);
  }, []);

  const closeCorporateHistoryModal = useCallback(() => {
    setIsHistoryModalOpen(false);
    setHistoryRows([]);
    setHistoryTotalCount(0);
    setHistoryLoading(false);
    setHistoryError("");
    setHistoryFilters({ ...HISTORY_FILTER_DEFAULTS });
    setHistoryAppliedFilters({ ...HISTORY_FILTER_DEFAULTS });
    setHistorySortBy("lastupdate_date");
    setHistorySortDirection("desc");
    setHistoryItemsPerPage(10);
    setHistoryCurrentPage(1);
    setHistoryUpdateForOptions([]);
    setHistoryUpdatedByOptions([]);
    setHistoryExportingCsv(false);
    setHistoryExportingExcel(false);
    setHistoryRefreshingAttachmentId(null);
    setHistoryCorp(null);
  }, []);

  useEffect(() => {
    const onEsc = (e) => {
      if (e.key !== "Escape") return;
      if (openActionMenu) closeActionMenu();
      if (isHistoryModalOpen) closeCorporateHistoryModal();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [
    closeActionMenu,
    closeCorporateHistoryModal,
    isHistoryModalOpen,
    openActionMenu,
  ]);

  const handleApplyHistoryFilters = useCallback(() => {
    setHistoryAppliedFilters({ ...historyFilters });
    setHistoryCurrentPage(1);
  }, [historyFilters]);

  const handleResetHistoryFilters = useCallback(() => {
    setHistoryFilters({ ...HISTORY_FILTER_DEFAULTS });
    setHistoryAppliedFilters({ ...HISTORY_FILTER_DEFAULTS });
    setHistorySortBy("lastupdate_date");
    setHistorySortDirection("desc");
    setHistoryItemsPerPage(10);
    setHistoryCurrentPage(1);
  }, []);

  const fetchHistoryRowsForExport = useCallback(async () => {
    if (!historyCorp?.corp_code) return [];
    return loadCorporateMovementHistory(
      historyCorp.corp_code,
      buildHistoryRequestParams({ forExport: true }),
      { silent: true }
    );
  }, [buildHistoryRequestParams, historyCorp, loadCorporateMovementHistory]);

  const handleExportHistoryCsv = useCallback(async () => {
    if (!historyCorp?.corp_code) return;

    setHistoryExportingCsv(true);
    try {
      const exportRows = await fetchHistoryRowsForExport();
      if (!exportRows || !exportRows.length) {
        toast.info("No corporate movement history to export.");
        return;
      }

      exportMovementHistoryToCsv(
        exportRows,
        `corporate_movement_history_${historyCorp.corp_code}_${exportStamp()}.csv`
      );
    } catch (error) {
      toast.error(`Failed to export CSV: ${error.message}`);
    } finally {
      setHistoryExportingCsv(false);
    }
  }, [fetchHistoryRowsForExport, historyCorp]);

  const handleExportHistoryExcel = useCallback(async () => {
    if (!historyCorp?.corp_code) return;

    setHistoryExportingExcel(true);
    try {
      const exportRows = await fetchHistoryRowsForExport();
      if (!exportRows || !exportRows.length) {
        toast.info("No corporate movement history to export.");
        return;
      }

      await exportMovementHistoryToExcel(
        exportRows,
        `corporate_movement_history_${historyCorp.corp_code}_${exportStamp()}.xlsx`
      );
    } catch (error) {
      toast.error(`Failed to export Excel: ${error.message}`);
    } finally {
      setHistoryExportingExcel(false);
    }
  }, [fetchHistoryRowsForExport, historyCorp]);

  const historyTotalPages = useMemo(
    () => Math.max(1, Math.ceil(historyTotalCount / historyItemsPerPage)),
    [historyItemsPerPage, historyTotalCount]
  );

  const handleHistoryPageChange = useCallback(
    (page) => {
      if (page >= 1 && page <= historyTotalPages) {
        setHistoryCurrentPage(page);
      }
    },
    [historyTotalPages]
  );

  useEffect(() => {
    if (!isHistoryModalOpen || !historyCorp?.corp_code) return;
    loadCorporateMovementHistory(
      historyCorp.corp_code,
      buildHistoryRequestParams()
    );
  }, [
    buildHistoryRequestParams,
    historyAppliedFilters,
    historyCorp,
    historyCurrentPage,
    historyItemsPerPage,
    historySortBy,
    historySortDirection,
    isHistoryModalOpen,
    loadCorporateMovementHistory,
  ]);

  useEffect(() => {
    if (historyCurrentPage > historyTotalPages) {
      setHistoryCurrentPage(historyTotalPages);
    }
  }, [historyCurrentPage, historyTotalPages]);

  const fundHistoryColumns = useMemo(
    () => [
      {
        Header: "Amount",
        accessor: "amount",
        Cell: ({ value }) => (
          <span className="text-gray-900">{value || "N/A"}</span>
        ),
      },
      {
        Header: "Date",
        accessor: "date",
        Cell: ({ value }) => (
          <span className="text-gray-700">{value || "N/A"}</span>
        ),
      },
      {
        Header: "Added By",
        accessor: "added_by",
        Cell: ({ value }) => (
          <span className="text-gray-700">{value || "N/A"}</span>
        ),
      },
      {
        Header: "Bank Ref#",
        accessor: "bank_ref",
        Cell: ({ value }) => (
          <span className="text-gray-700">{value || "N/A"}</span>
        ),
      },
      {
        Header: "Remarks",
        accessor: "remarks",
        Cell: ({ value }) => (
          <span className="text-gray-700">{value || "N/A"}</span>
        ),
      },
      {
        Header: "Fund Name",
        accessor: "fund_name",
        Cell: ({ value }) => (
          <span className="text-gray-700">{value || "N/A"}</span>
        ),
      },
      {
        Header: "Fund Mobile",
        accessor: "fund_mobile",
        Cell: ({ value }) => (
          <span className="text-gray-700">{value || "N/A"}</span>
        ),
      },
      {
        Header: "Fund Email",
        accessor: "fund_email",
        Cell: ({ value }) => (
          <span className="text-gray-700">{value || "N/A"}</span>
        ),
      },
      {
        Header: "Fund Mgmt",
        accessor: "fund_mgmt_flag",
        Cell: ({ value }) => (
          <span className="text-gray-700">
            {formatFundManagementFlag(value)}
          </span>
        ),
      },
      {
        Header: "Revolving Fund",
        accessor: "revolving_fund_amt",
        Cell: ({ value }) => (
          <span className="text-gray-700">{formatCurrency(value)}</span>
        ),
      },
      {
        Header: "Threshold",
        accessor: "threshold_amt",
        Cell: ({ value }) => (
          <span className="text-gray-700">{formatCurrency(value)}</span>
        ),
      },
    ],
    []
  );

  const {
    getTableProps: getFundHistoryTableProps,
    getTableBodyProps: getFundHistoryTableBodyProps,
    headerGroups: fundHistoryHeaderGroups,
    rows: fundHistoryRows,
    prepareRow: prepareFundHistoryRow,
  } = useTable(
    {
      columns: fundHistoryColumns,
      data: fundHistory,
      initialState: { pageIndex: fundHistoryPage - 1, pageSize: fundHistoryPerPage },
      manualPagination: true,
      pageCount: Math.ceil(fundHistoryTotal / fundHistoryPerPage),
    },
    usePagination
  );

  const columns = useMemo(
    () => [
      {
        Header: "Corp Code",
        accessor: "corp_code",
        Cell: ({ value }) => (
          <span className="font-medium text-gray-900">{value || "N/A"}</span>
        ),
      },
      {
        Header: "Corp Prefix",
        accessor: "corp_prefix",
        Cell: ({ value }) => (
          <span className="text-gray-700">{value || "N/A"}</span>
        ),
      },
      {
        Header: "Corp Name",
        accessor: "corp_name",
        Cell: ({ value }) => (
          <span className="text-gray-900">{value || "N/A"}</span>
        ),
      },
      {
        Header: "Location",
        accessor: "location",
        Cell: ({ value }) => (
          <span className="text-gray-600">{value || "N/A"}</span>
        ),
      },
      {
        Header: "Contact Person",
        accessor: "contact_person",
        Cell: ({ value }) => (
          <span className="text-gray-700">{value || "N/A"}</span>
        ),
      },
      {
        Header: "Members",
        accessor: "member_status_summary",
        disableSortBy: true,
        Cell: ({ row }) => {
          const corp = row.original;
          return (
            <textarea
              readOnly
              tabIndex={-1}
              aria-label={`Member count for corporate ${corp.corp_code || ""}`}
              value={formatMemberStatusSummaryText(corp)}
              className="h-16 w-56 resize-none rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs leading-5 text-gray-700 shadow-inner focus:outline-none"
            />
          );
        },
      },
      {
        Header: "Main/Sub Corp Type",
        accessor: "main_sub",
        Cell: ({ value }) => {
          const status = mainSubStatusMap[value] || mainSubStatusMap.default;
          return <Badge color={status.color}>{status.name}</Badge>;
        },
      },
      {
        Header: "Finalize Status",
        accessor: "finalize_status",
        Cell: ({ value }) => {
          const status = finalizeStatusMap[value] || finalizeStatusMap.default;
          return <Badge color={status.color}>{status.name}</Badge>;
        },
      },
      {
        Header: "Status",
        accessor: "status_flag",
        Cell: ({ value }) => {
          const status = statusMap[value] || statusMap.default;
          return <Badge color={status.color}>{status.name}</Badge>;
        },
      },
      {
        Header: "Actions",
        accessor: "actions",
        disableSortBy: true,
        Cell: ({ row }) => {
          const corp = row.original;
          const rowKey = getCorporateActionKey(corp);
          const actions = [
            {
              key: "view",
              label: "View details",
              Icon: EyeIcon,
              color: "text-sky-600",
              onClick: () => setViewCorpCode(corp.corp_code),
            },
            {
              key: "edit",
              label: "Edit corporate",
              Icon: PencilSquareIcon,
              color: "text-blue-600",
              onClick: () => navigate(`/corporate/edit/${corp.corp_code}`),
            },
            {
              key: "history",
              label: "Movement history",
              Icon: ClockIcon,
              color: "text-violet-600",
              onClick: () => openCorporateHistoryModal(corp),
            },
            ...(corp.finalize_status !== "finalized"
              ? [
                  {
                    key: "finalize",
                    label: "Finalize corporate",
                    Icon: DocumentCheckIcon,
                    color: "text-slate-600",
                    onClick: () => {
                      setFinalizeCorpCode(corp.corp_code);
                      setFinalizeCorpName(corp.corp_name);
                      setFinalizePassword("");
                      setFinalizeModalOpen(true);
                    },
                  },
                ]
              : [
                  {
                    key: "fund",
                    label: "Add corporate fund",
                    Icon: CurrencyDollarIcon,
                    color: "text-emerald-600",
                    onClick: () => openAddFundModal(corp),
                  },
                ]),
            {
              key: "status",
              label: "Change status",
              Icon: Cog6ToothIcon,
              color: "text-indigo-600",
              onClick: () => openModal(corp),
            },
          ];

          return (
            <div className="relative">
              <button
                type="button"
                aria-label={`Actions for corporate ${corp.corp_code || ""}`}
                onClick={(event) => handleActionMenuToggle(corp, event)}
                className={clsx(
                  "inline-flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-xs font-semibold shadow-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1",
                  openActionMenu === rowKey
                    ? "border-blue-400 bg-blue-50 text-blue-700 shadow-blue-100"
                    : "border-gray-300 bg-white text-gray-700 hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                )}
              >
                <EllipsisVerticalIcon className="h-4 w-4 text-gray-500" />
                <span>Actions</span>
              </button>
              {openActionMenu === rowKey &&
                actionMenuPosition &&
                typeof document !== "undefined" &&
                createPortal(
                  <>
                    <button
                      type="button"
                      aria-label="Close corporate actions menu"
                      className="fixed inset-0 z-[9998] cursor-default bg-transparent"
                      onClick={closeActionMenu}
                    />
                    <div
                      className="fixed z-[9999] w-72 overflow-visible"
                      style={{
                        top: `${actionMenuPosition.top}px`,
                        left: `${actionMenuPosition.left}px`,
                      }}
                    >
                      <div
                        className={clsx(
                          "absolute right-6 h-3 w-3 rotate-45 border bg-white",
                          actionMenuPosition.placement === "top"
                            ? "-bottom-1.5 border-l-0 border-t-0 border-gray-200"
                            : "-top-1.5 border-b-0 border-r-0 border-gray-200"
                        )}
                      />
                      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl shadow-slate-900/15 ring-1 ring-black/5">
                        <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50 to-white px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Corporate Actions
                          </p>
                        </div>
                        <div className="space-y-1 p-2">
                          {actions.map((action) => (
                            <button
                              key={action.key}
                              type="button"
                              onClick={() => {
                                action.onClick();
                                closeActionMenu();
                              }}
                              className="group flex w-full items-center justify-between rounded-lg border border-transparent px-3 py-2.5 text-left text-sm text-gray-700 transition-all duration-150 hover:translate-x-0.5 hover:border-blue-100 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 hover:text-blue-700 hover:shadow-sm"
                            >
                              <span className="flex min-w-0 items-center">
                                <span className="mr-3 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 transition-all duration-150 group-hover:bg-white group-hover:shadow-sm">
                                  <action.Icon
                                    className={clsx("h-4 w-4", action.color)}
                                  />
                                </span>
                                <span className="truncate font-medium">
                                  {action.label}
                                </span>
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>,
                  document.body
                )}
            </div>
          );
        },
      },
    ],
    [
      actionMenuPosition,
      closeActionMenu,
      handleActionMenuToggle,
      navigate,
      openActionMenu,
      openCorporateHistoryModal,
    ]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
        initialState: { pageIndex: currentPage - 1, pageSize: itemsPerPage },
        manualPagination: true,
        pageCount: Math.ceil(totalCount / itemsPerPage),
        disableSortRemove: true,
      },
      usePagination
    );

  const handleSort = (column) => {
    if (!column || !column.id) return;
    if (column.disableSortBy) return;
    if (sortBy === column.id) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column.id);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const getPageNumbers = () => {
    const maxPagesToShow = 5;
    const startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    return Array.from(
      { length: endPage - startPage + 1 },
      (_, i) => startPage + i
    );
  };

  const fundHistoryTotalPages = Math.max(
    1,
    Math.ceil(fundHistoryTotal / fundHistoryPerPage)
  );
  const addFundInfo = addFundInformation || {};
  const addFundRevolvingFund =
    addFundInfo.revolving_fund ??
    addFundInfo._revolving_fund_amt ??
    addFundInfo.revolving_fund_amt;
  const addFundRemainingFund =
    addFundInfo.remaining_fund ??
    addFundInfo.remaining_fund_amt ??
    remainingFund;
  const addFundThreshold =
    addFundInfo.threshold ??
    addFundInfo._threshold_amt ??
    addFundInfo.threshold_amt;
  const addFundFirstWarning =
    addFundInfo.first_warning_amount ?? addFundInfo._first_warning_amount;

  const getFundHistoryPageNumbers = () => {
    const maxPagesToShow = 5;
    const startPage = Math.max(
      1,
      fundHistoryPage - Math.floor(maxPagesToShow / 2)
    );
    const endPage = Math.min(
      fundHistoryTotalPages,
      startPage + maxPagesToShow - 1
    );
    return Array.from(
      { length: endPage - startPage + 1 },
      (_, i) => startPage + i
    );
  };

  const getHistoryPageNumbers = () => {
    const maxPagesToShow = 5;
    const startPage = Math.max(
      1,
      historyCurrentPage - Math.floor(maxPagesToShow / 2)
    );
    const endPage = Math.min(
      historyTotalPages,
      startPage + maxPagesToShow - 1
    );
    return Array.from(
      { length: endPage - startPage + 1 },
      (_, i) => startPage + i
    );
  };

  return (
    <div className="mx-auto w-full max-w-1xl p-4">
      <ToastContainer />

      {/* Toolbar */}
      <div className="mb-4 rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="flex items-center justify-between rounded-t-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 px-4 py-3 text-white">
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-white/10 p-2 ring-1 ring-white/20 backdrop-blur">
              <FontAwesomeIcon icon={faBuilding} className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-base font-semibold">Corporate Management</h1>
              <div className="text-xs text-white/90">
                {totalCount.toLocaleString()} total record
                {totalCount === 1 ? "" : "s"}
              </div>
            </div>
          </div>
          <button
            onClick={() => navigate("/corporate/add")}
            className="inline-flex items-center gap-2 rounded-md bg-white/10 px-3 py-2 text-sm font-medium ring-1 ring-white/20 hover:bg-white/20"
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>Add New Corporate</span>
          </button>
        </div>

        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-5">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search corporates..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full rounded-md border border-gray-200 bg-white pl-9 pr-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="rounded-md border border-gray-200 px-2 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
          >
            <option value="ALL">All Statuses</option>
            {Object.entries(statusMap).map(([key, { name }]) =>
              key !== "default" ? (
                <option key={key} value={key}>
                  {name}
                </option>
              ) : null
            )}
          </select>
          <select
            value={mainSubFilter}
            onChange={(e) => {
              setMainSubFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="rounded-md border border-gray-200 px-2 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
          >
            <option value="ALL">All Main/Sub Corp Type</option>
            {Object.entries(mainSubStatusMap).map(([key, { name }]) =>
              key !== "default" ? (
                <option key={key} value={key}>
                  {name}
                </option>
              ) : null
            )}
          </select>

          <select
            value={corpPrefixFilter}
            onChange={(e) => {
              setCorpPrefixFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="rounded-md border border-gray-200 px-2 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
          >
            <option value="ALL">All Prefixes</option>
            {corpPrefixes.map((prefix) => (
              <option key={prefix} value={prefix}>
                {prefix}
              </option>
            ))}
          </select>

          <div className="hidden items-center justify-end text-sm text-gray-600 lg:flex">
            Page {currentPage} of {totalPages}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="max-h-[60vh] overflow-auto">
          {loading ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="sticky top-0 bg-gray-50">
                {[
                  "Corp Code",
                  "Corp Prefix",
                  "Corp Name",
                  "Location",
                  "Contact Person",
                  "Members",
                  "Main/Sub Corp Type",
                  "Finalize Status",
                  "Status",
                  "Actions",
                ].map((h) => (
                  <tr key={`head-${h}`}>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                      {h}
                    </th>
                  </tr>
                ))}
              </thead>
              <tbody>
                {[...Array(8)].map((_, idx) => (
                  <tr key={idx} className="border-t">
                    {[...Array(10)].map((__, i) => (
                      <td key={i} className="px-4 py-3">
                        <Skeleton height={16} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : data.length === 0 ? (
            <div className="p-8">
              <EmptyState
                title="No corporates found"
                subtitle="Try changing your filters or search."
              />
            </div>
          ) : (
            <table
              {...getTableProps()}
              className="min-w-full divide-y divide-gray-200"
            >
              <thead className="sticky top-0 bg-gray-50">
                {headerGroups.map((headerGroup, i) => (
                  <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        key={column.id}
                        {...column.getHeaderProps()}
                        onClick={() => handleSort(column)}
                        className={clsx(
                          "px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600",
                          "cursor-pointer select-none"
                        )}
                      >
                        <div className="inline-flex items-center gap-1">
                          {column.render("Header")}
                          {column.id === sortBy && (
                            <span className="text-gray-400">
                              {sortDirection === "asc" ? "▲" : "▼"}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody
                {...getTableBodyProps()}
                className="divide-y divide-gray-100"
              >
                {rows.map((row) => {
                  prepareRow(row);
                  return (
                    <tr
                      {...row.getRowProps()}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      {row.cells.map((cell) => (
                        <td
                          key={cell.column.id}
                          {...cell.getCellProps()}
                          className="px-4 py-3 text-sm text-gray-800"
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        <div className="flex flex-col items-center justify-between gap-3 border-t border-gray-100 p-3 text-sm text-gray-700 sm:flex-row">
          <div className="text-xs text-gray-600">
            Showing {totalCount ? (currentPage - 1) * itemsPerPage + 1 : 0} to{" "}
            {Math.min(currentPage * itemsPerPage, totalCount)} of{" "}
            {totalCount.toLocaleString()} entries
          </div>
          <div className="flex flex-wrap items-center gap-1">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className={clsx(
                "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                currentPage === 1
                  ? "bg-gray-100 text-gray-400"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              )}
            >
              <ChevronDoubleLeftIcon className="h-5 w-5" />
              First
            </button>
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className={clsx(
                "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                currentPage === 1
                  ? "bg-gray-100 text-gray-400"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              )}
            >
              <ChevronLeftIcon className="h-5 w-5" />
              Previous
            </button>
            {getPageNumbers().map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={clsx(
                  "rounded-md px-2 py-1 text-xs",
                  currentPage === page
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                )}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className={clsx(
                "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                currentPage === totalPages
                  ? "bg-gray-100 text-gray-400"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              )}
            >
              Next
              <ChevronRightIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className={clsx(
                "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                currentPage === totalPages
                  ? "bg-gray-100 text-gray-400"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              )}
            >
              Last
              <ChevronDoubleRightIcon className="h-5 w-5" />
            </button>

            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="ml-1 rounded-md border border-gray-200 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            >
              {[10, 20, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Change Status Modal */}
      <AnimatePresence>
        {isModalOpen && selectedCorp && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={closeModal}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, y: 10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
              className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl"
            >
              <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 px-4 py-3 text-white">
                <h2 className="text-base font-semibold">
                  Change Status for {selectedCorp.corp_name}
                </h2>
                <div className="text-xs text-white/80">
                  {selectedCorp.corp_code}
                </div>
              </div>
              <div className="p-4">
                <form onSubmit={handleChangeStatus} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      New Status *
                    </label>
                    <select
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                      className="mt-1 block w-full rounded-md border border-gray-200 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    >
                      <option value="">Select new status</option>
                      {Object.entries(statusMap).map(([key, { name }]) =>
                        key !== "default" &&
                        key !== selectedCorp.status_flag?.toString() ? (
                          <option key={key} value={key}>
                            {name}
                          </option>
                        ) : null
                      )}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Reason *
                    </label>
                    <textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      rows={4}
                      placeholder="Enter reason for status change"
                      className="mt-1 block w-full rounded-md border border-gray-200 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Attach Files (Optional, max 5)
                    </label>
                    <input
                      type="file"
                      multiple
                      accept=".png,.jpeg,.jpg,.bmp,.pdf,.doc,.docx,.xls,.xlsx,.csv"
                      onChange={handleFileChange}
                      className="mt-1 block w-full text-sm text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-blue-50 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-blue-700 hover:file:bg-blue-100"
                    />
                    {files.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {files.map((f, i) => (
                          <li
                            key={`${f.name}-${i}`}
                            className="truncate text-xs text-gray-600"
                          >
                            • {f.name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="rounded-md bg-gray-100 px-4 py-2 text-sm text-gray-800 hover:bg-gray-200"
                      disabled={modalLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={modalLoading}
                      className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                    >
                      {modalLoading ? (
                        <>
                          <svg
                            className="h-5 w-5 animate-spin text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            />
                          </svg>
                          Updating…
                        </>
                      ) : (
                        "Update Status"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Finalize Modal */}
      <AnimatePresence>
        {finalizeModalOpen && finalizeCorpCode && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setFinalizeModalOpen(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, y: 10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
              className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl"
            >
              <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 px-4 py-3 text-white">
                <h2 className="text-base font-semibold">
                  Finalize {finalizeCorpName}
                </h2>
                <div className="text-xs text-white/80">{finalizeCorpCode}</div>
              </div>

              <div className="p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  For security purposes, please re-enter your account password.
                </h3>

                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!finalizePassword.trim()) {
                      toast.error("Password is required.");
                      return;
                    }
                    setFinalizeLoading(true);
                    try {
                      const res = await axios.post(
                        `${BASE_URL}/corporates/${finalizeCorpCode}/finalize`,
                        { password: finalizePassword },
                        { headers: getAuthHeaders() }
                      );
                      toast.success(res.data.message || "Corporate finalized.");
                      setFinalizeModalOpen(false);
                      fetchCorporates();
                    } catch (err) {
                      const msg = err.response?.data?.message || err.message;
                      toast.error("Failed to finalize: " + msg);
                    } finally {
                      setFinalizeLoading(false);
                      setFinalizePassword("");
                    }
                  }}
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Password *
                    </label>
                    <input
                      type="password"
                      value={finalizePassword}
                      onChange={(e) => setFinalizePassword(e.target.value)}
                      className="mt-1 block w-full rounded-md border border-gray-200 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                      required
                    />
                  </div>

                  <div className="flex justify-end gap-2 mt-4">
                    <button
                      type="button"
                      onClick={() => setFinalizeModalOpen(false)}
                      disabled={finalizeLoading}
                      className="rounded-md bg-gray-100 px-4 py-2 text-sm text-gray-800 hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={finalizeLoading}
                      className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                    >
                      {finalizeLoading ? "Finalizing…" : "Confirm"}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Fund Modal */}
      <AnimatePresence>
        {addFundModalOpen && addFundCorpCode && (
          <motion.div
            className={clsx(
              "fixed inset-0 z-50 flex bg-black/50 p-4",
              addFundMaximized
                ? "items-stretch justify-center"
                : "items-center justify-center"
            )}
            onClick={closeAddFundModal}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, y: 10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
              className={clsx(
                "flex w-full flex-col overflow-hidden rounded-2xl bg-white shadow-2xl",
                addFundMaximized
                  ? "h-[calc(100vh-2rem)] max-w-[calc(100vw-2rem)]"
                  : "max-h-[calc(100vh-2rem)] max-w-4xl"
              )}
            >
              <div className="flex items-center justify-between gap-3 bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 px-4 py-3 text-white">
                <div>
                  <h2 className="text-base font-semibold">
                    Add Corporate Fund - {addFundCorpName}
                  </h2>
                  <div className="text-xs text-white/80">
                    Remaining Fund: {formatCurrency(addFundRemainingFund)}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      navigate(`/corporate/add-fund/${addFundCorpCode}`, {
                        state: { corpName: addFundCorpName },
                      })
                    }
                    className="relative inline-flex items-center gap-2 rounded-md bg-white/10 px-3 py-2 text-xs font-medium text-white hover:bg-white/20"
                  >
                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                    <span>View in Full page</span>
                    <span className="absolute -right-2 -top-2 rounded-full bg-emerald-400 px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none text-emerald-950 ring-1 ring-white">
                      New
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setAddFundMaximized((current) => !current)}
                    className="rounded-md p-1 text-white/80 hover:bg-white/10 hover:text-white"
                    aria-label={
                      addFundMaximized
                        ? "Minimize Add Corporate Fund modal"
                        : "Maximize Add Corporate Fund modal"
                    }
                    title={addFundMaximized ? "Minimize" : "Maximize"}
                  >
                    {addFundMaximized ? (
                      <ArrowsPointingInIcon className="h-5 w-5" />
                    ) : (
                      <ArrowsPointingOutIcon className="h-5 w-5" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={closeAddFundModal}
                    className="rounded-md p-1 text-white/80 hover:bg-white/10 hover:text-white"
                    aria-label="Close Add Corporate Fund modal"
                    title="Close"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div
                className={clsx(
                  "p-6",
                  addFundMaximized
                    ? "flex-1 overflow-y-auto"
                    : "max-h-[calc(100vh-9rem)] overflow-y-auto"
                )}
              >
                <div className="mb-6 rounded-xl border border-gray-100 bg-gray-50 p-4 shadow-sm">
                  <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <h3 className="text-sm font-semibold text-gray-900">
                      Fund Information
                    </h3>
                    <Badge
                      color={addFundInfo.fund_mgmt_flag ? "green" : "gray"}
                    >
                      Fund Mgmt:{" "}
                      {addFundInfo.fund_mgmt_flag ? "Enabled" : "Disabled"}
                    </Badge>
                  </div>

                  {addFundInfoLoading ? (
                    <div className="flex items-center justify-center rounded-lg border border-dashed border-gray-200 bg-white py-8">
                      <ArrowPathIcon className="h-5 w-5 animate-spin text-blue-600" />
                      <span className="ml-2 text-sm text-gray-600">
                        Loading fund details...
                      </span>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
                      <StatCard
                        icon={CurrencyDollarIcon}
                        label="Revolving Fund"
                        value={formatCurrency(addFundRevolvingFund)}
                      />
                      <StatCard
                        icon={CurrencyDollarIcon}
                        label="Total Added Fund"
                        value={formatCurrency(
                          addFundInfo.total_added_fund ?? addFundInfo._corp_fund
                        )}
                      />
                      <StatCard
                        icon={CurrencyDollarIcon}
                        label="Group Utilization"
                        value={formatCurrency(
                          addFundInfo.group_utilization ?? addFundInfo.total_util
                        )}
                      />
                      <StatCard
                        icon={CurrencyDollarIcon}
                        label="Group Reimbursement"
                        value={formatCurrency(
                          addFundInfo.group_reimbursement ??
                            addFundInfo.group_reim_util
                        )}
                      />
                      <StatCard
                        icon={CurrencyDollarIcon}
                        label="Account Utilization"
                        value={formatCurrency(
                          addFundInfo.account_utilization ??
                            addFundInfo.acct_util
                        )}
                      />
                      <StatCard
                        icon={CurrencyDollarIcon}
                        label="Account Reimbursement"
                        value={formatCurrency(
                          addFundInfo.account_reimbursement ??
                            addFundInfo.acct_reim_util
                        )}
                      />
                      <StatCard
                        icon={CurrencyDollarIcon}
                        label="Threshold"
                        value={formatCurrency(addFundThreshold)}
                      />
                      <StatCard
                        icon={CurrencyDollarIcon}
                        label="Remaining Fund"
                        value={formatCurrency(addFundRemainingFund)}
                      />
                      <StatCard
                        icon={ClockIcon}
                        label="Util %"
                        value={formatFundUtilPercent(
                          addFundRevolvingFund,
                          addFundRemainingFund
                        )}
                      />
                      <StatCard
                        icon={CurrencyDollarIcon}
                        label="1st Warning Amount"
                        value={formatCurrency(addFundFirstWarning)}
                      />
                    </div>
                  )}
                </div>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleAddFund();
                  }}
                  className="space-y-4 mb-6"
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Enter Amount *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={addFundAmount}
                        onChange={(e) => setAddFundAmount(e.target.value)}
                        className="mt-1 block w-full rounded-md border border-gray-200 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Enter Bank Ref# *
                      </label>
                      <input
                        type="text"
                        value={addFundBankRef}
                        onChange={(e) => setAddFundBankRef(e.target.value)}
                        className="mt-1 block w-full rounded-md border border-gray-200 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Enter Remarks *
                      </label>
                      <input
                        type="text"
                        value={addFundRemarks}
                        onChange={(e) => setAddFundRemarks(e.target.value)}
                        className="mt-1 block w-full rounded-md border border-gray-200 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        required
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={addFundLoading}
                      className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                    >
                      {addFundLoading ? (
                        <>
                          <svg
                            className="h-5 w-5 animate-spin text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            />
                          </svg>
                          Adding Fund…
                        </>
                      ) : (
                        "Add Fund"
                      )}
                    </button>
                  </div>
                </form>

                {/* Fund History Table */}
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-sm font-medium text-gray-700">
                      Corporate Fund History
                    </h3>
                    <div className="flex gap-2">
                      <div className="relative">
                        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search fund history..."
                          value={fundHistorySearch}
                          onChange={(e) => {
                            setFundHistorySearch(e.target.value);
                            setFundHistoryPage(1);
                          }}
                          className="w-64 rounded-md border border-gray-200 bg-white pl-9 pr-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => handleExportFundHistory("csv")}
                        disabled={
                          fundHistoryExportingCsv || fundHistoryExportingExcel
                        }
                        className="inline-flex items-center gap-2 rounded-md bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700 ring-1 ring-emerald-200 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4" />
                        {fundHistoryExportingCsv
                          ? "Exporting CSV..."
                          : "Export CSV"}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleExportFundHistory("xlsx")}
                        disabled={
                          fundHistoryExportingCsv || fundHistoryExportingExcel
                        }
                        className="inline-flex items-center gap-2 rounded-md bg-indigo-50 px-4 py-2 text-sm font-medium text-indigo-700 ring-1 ring-indigo-200 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4" />
                        {fundHistoryExportingExcel
                          ? "Exporting Excel..."
                          : "Export Excel"}
                      </button>
                    </div>
                  </div>

                  <div className="max-h-[40vh] overflow-auto">
                    {fundHistory.length === 0 ? (
                      <EmptyState
                        title="No fund history found"
                        subtitle="No fund transactions available."
                      />
                    ) : (
                      <table
                        {...getFundHistoryTableProps()}
                        className="min-w-full divide-y divide-gray-200"
                      >
                        <thead className="sticky top-0 bg-gray-50">
                          {fundHistoryHeaderGroups.map((headerGroup, i) => (
                            <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                              {headerGroup.headers.map((column) => (
                                <th
                                  key={column.id}
                                  {...column.getHeaderProps()}
                                  className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600"
                                >
                                  {column.render("Header")}
                                </th>
                              ))}
                            </tr>
                          ))}
                        </thead>
                        <tbody
                          {...getFundHistoryTableBodyProps()}
                          className="divide-y divide-gray-100"
                        >
                          {fundHistoryRows.map((row) => {
                            prepareFundHistoryRow(row);
                            return (
                              <tr
                                {...row.getRowProps()}
                                className="hover:bg-gray-50 transition-colors"
                              >
                                {row.cells.map((cell) => (
                                  <td
                                    key={cell.column.id}
                                    {...cell.getCellProps()}
                                    className="px-4 py-3 text-sm text-gray-800"
                                  >
                                    {cell.render("Cell")}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Fund History Pagination */}
                  <div className="flex flex-col items-center justify-between gap-3 border-t border-gray-100 p-3 text-sm text-gray-700 sm:flex-row">
                    <div className="text-xs text-gray-600">
                      Showing{" "}
                      {fundHistoryTotal
                        ? (fundHistoryPage - 1) * fundHistoryPerPage + 1
                        : 0}{" "}
                      to{" "}
                      {Math.min(
                        fundHistoryPage * fundHistoryPerPage,
                        fundHistoryTotal
                      )}{" "}
                      of {fundHistoryTotal.toLocaleString()} entries
                    </div>
                    <div className="flex flex-wrap items-center gap-1">
                      <button
                        onClick={() => setFundHistoryPage(1)}
                        disabled={fundHistoryPage === 1}
                        className={clsx(
                          "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                          fundHistoryPage === 1
                            ? "bg-gray-100 text-gray-400"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        )}
                      >
                        <ChevronDoubleLeftIcon className="h-5 w-5" />
                        First
                      </button>
                      <button
                        onClick={() =>
                          setFundHistoryPage((p) => Math.max(1, p - 1))
                        }
                        disabled={fundHistoryPage === 1}
                        className={clsx(
                          "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                          fundHistoryPage === 1
                            ? "bg-gray-100 text-gray-400"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        )}
                      >
                        <ChevronLeftIcon className="h-5 w-5" />
                        Previous
                      </button>
                      {getFundHistoryPageNumbers().map((page) => (
                        <button
                          key={page}
                          onClick={() => setFundHistoryPage(page)}
                          className={clsx(
                            "rounded-md px-2 py-1 text-xs",
                            fundHistoryPage === page
                              ? "bg-blue-600 text-white"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          )}
                        >
                          {page}
                        </button>
                      ))}
                      <button
                        onClick={() =>
                          setFundHistoryPage((p) =>
                            Math.min(fundHistoryTotalPages, p + 1)
                          )
                        }
                        disabled={fundHistoryPage === fundHistoryTotalPages}
                        className={clsx(
                          "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                          fundHistoryPage === fundHistoryTotalPages
                            ? "bg-gray-100 text-gray-400"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        )}
                      >
                        Next
                        <ChevronRightIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => setFundHistoryPage(fundHistoryTotalPages)}
                        disabled={fundHistoryPage === fundHistoryTotalPages}
                        className={clsx(
                          "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                          fundHistoryPage === fundHistoryTotalPages
                            ? "bg-gray-100 text-gray-400"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        )}
                      >
                        Last
                        <ChevronDoubleRightIcon className="h-5 w-5" />
                      </button>

                      <select
                        value={fundHistoryPerPage}
                        onChange={(e) => {
                          setFundHistoryPerPage(Number(e.target.value));
                          setFundHistoryPage(1);
                        }}
                        className="ml-1 rounded-md border border-gray-200 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                      >
                        {[10, 20, 50, 100].map((size) => (
                          <option key={size} value={size}>
                            {size} per page
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirm Add Fund Modal */}
      <AnimatePresence>
        {showConfirmModal && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowConfirmModal(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, y: 10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
              className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl"
            >
              <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 px-4 py-3 text-white">
                <h2 className="text-base font-semibold">
                  Confirm Add Fund
                </h2>
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-700 mb-4">
                  Are you sure you want to add fund to {addFundCorpName}? The following contact person will be notified.
                </p>
                {fundContacts.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700">
                      Contact Person: {fundContacts.map(c => c.fund_name).join(", ")}
                    </p>
                    <p className="text-sm font-medium text-gray-700">
                      Email: {fundContacts.map(c => c.fund_email).join(", ")}
                    </p>
                    <p className="text-sm font-medium text-gray-700">
                      Mobile#: {fundContacts.map(c => c.fund_mobile).join(", ")}
                    </p>
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowConfirmModal(false)}
                    disabled={addFundLoading}
                    className="rounded-md bg-gray-100 px-4 py-2 text-sm text-gray-800 hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmAddFund}
                    disabled={addFundLoading}
                    className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                  >
                    {addFundLoading ? (
                      <>
                        <svg
                          className="h-5 w-5 animate-spin text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          />
                        </svg>
                        Adding…
                      </>
                    ) : (
                      "Yes"
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Corporate Movement History Modal */}
      <AnimatePresence>
        {isHistoryModalOpen && historyCorp ? (
          <motion.div
            className="fixed inset-0 z-[145] flex items-center justify-center bg-slate-900/60 px-4 py-6 backdrop-blur-[1px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="flex max-h-[94vh] w-full max-w-7xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
              initial={{ y: 18, opacity: 0, scale: 0.99 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 18, opacity: 0, scale: 0.99 }}
              transition={{ duration: 0.2 }}
            >
              <div className="border-b border-slate-200 bg-gradient-to-r from-blue-50 via-sky-50 to-cyan-50 px-5 py-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h3 className="text-base font-semibold text-slate-900 sm:text-lg">
                      Corporate Movement History
                    </h3>
                    <p className="text-xs text-slate-600 sm:text-sm">
                      {historyCorp?.corp_name || "N/A"}{" "}
                      <span className="font-semibold text-slate-700">
                        ({historyCorp?.corp_code || "N/A"})
                      </span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleExportHistoryCsv}
                      disabled={
                        historyLoading ||
                        historyExportingCsv ||
                        historyExportingExcel
                      }
                      className="inline-flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60 sm:text-sm"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      {historyExportingCsv ? "Exporting CSV..." : "CSV"}
                    </button>
                    <button
                      type="button"
                      onClick={handleExportHistoryExcel}
                      disabled={
                        historyLoading ||
                        historyExportingCsv ||
                        historyExportingExcel
                      }
                      className="inline-flex items-center gap-2 rounded-lg border border-indigo-300 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-60 sm:text-sm"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      {historyExportingExcel ? "Exporting Excel..." : "Excel"}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        loadCorporateMovementHistory(
                          historyCorp?.corp_code,
                          buildHistoryRequestParams()
                        )
                      }
                      disabled={
                        historyLoading ||
                        historyExportingCsv ||
                        historyExportingExcel
                      }
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 sm:text-sm"
                    >
                      <ArrowPathIcon
                        className={clsx(
                          "h-4 w-4",
                          historyLoading && "animate-spin"
                        )}
                      />
                      Refresh
                    </button>
                    <button
                      type="button"
                      onClick={closeCorporateHistoryModal}
                      className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 sm:text-sm"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>

              <div className="border-b border-slate-200 bg-slate-50 px-5 py-4">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
                  <div className="xl:col-span-2">
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Search
                    </label>
                    <input
                      type="text"
                      value={historyFilters.search_string}
                      onChange={(e) =>
                        setHistoryFilters((prev) => ({
                          ...prev,
                          search_string: e.target.value,
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleApplyHistoryFilters();
                      }}
                      placeholder="Search Update For, Updated By, Reason, Remarks..."
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Update For
                    </label>
                    <select
                      value={historyFilters.update_for}
                      onChange={(e) =>
                        setHistoryFilters((prev) => ({
                          ...prev,
                          update_for: e.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    >
                      <option value="all">All</option>
                      {historyUpdateForOptions.map((option) => (
                        <option key={option} value={option}>
                          {getMovementUpdateForMeta(option).label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Updated By
                    </label>
                    <select
                      value={historyFilters.updated_by}
                      onChange={(e) =>
                        setHistoryFilters((prev) => ({
                          ...prev,
                          updated_by: e.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    >
                      <option value="all">All</option>
                      {historyUpdatedByOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Display per Page
                    </label>
                    <select
                      value={historyItemsPerPage}
                      onChange={(e) => {
                        setHistoryItemsPerPage(Number(e.target.value));
                        setHistoryCurrentPage(1);
                      }}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-6">
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Effective Date From
                    </label>
                    <input
                      type="date"
                      value={historyFilters.effective_date_from}
                      onChange={(e) =>
                        setHistoryFilters((prev) => ({
                          ...prev,
                          effective_date_from: e.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Effective Date To
                    </label>
                    <input
                      type="date"
                      value={historyFilters.effective_date_to}
                      onChange={(e) =>
                        setHistoryFilters((prev) => ({
                          ...prev,
                          effective_date_to: e.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Last Update From
                    </label>
                    <input
                      type="date"
                      value={historyFilters.lastupdate_date_from}
                      onChange={(e) =>
                        setHistoryFilters((prev) => ({
                          ...prev,
                          lastupdate_date_from: e.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Last Update To
                    </label>
                    <input
                      type="date"
                      value={historyFilters.lastupdate_date_to}
                      onChange={(e) =>
                        setHistoryFilters((prev) => ({
                          ...prev,
                          lastupdate_date_to: e.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Sort By
                    </label>
                    <select
                      value={historySortBy}
                      onChange={(e) => {
                        setHistorySortBy(e.target.value);
                        setHistoryCurrentPage(1);
                      }}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    >
                      <option value="lastupdate_date">Last Update</option>
                      <option value="effective_date">Effective Date</option>
                      <option value="update_for">Update For</option>
                      <option value="updated_by">Updated By</option>
                      <option value="reason">Reason</option>
                      <option value="remarks">Remarks</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Sort Direction
                    </label>
                    <select
                      value={historySortDirection}
                      onChange={(e) => {
                        setHistorySortDirection(e.target.value);
                        setHistoryCurrentPage(1);
                      }}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    >
                      <option value="desc">Descending</option>
                      <option value="asc">Ascending</option>
                    </select>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={handleResetHistoryFilters}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100 sm:text-sm"
                  >
                    Reset Filters
                  </button>
                  <button
                    type="button"
                    onClick={handleApplyHistoryFilters}
                    className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700 sm:text-sm"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-auto p-5">
                {historyLoading ? (
                  <Skeleton count={6} height={36} />
                ) : historyError ? (
                  <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                    {historyError}
                  </div>
                ) : historyRows.length === 0 ? (
                  <EmptyState
                    title="No movement history"
                    subtitle="No movement records found for this corporate."
                  />
                ) : (
                  <div className="overflow-auto rounded-xl border border-slate-200">
                    <table className="min-w-full divide-y divide-slate-200 text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-700">
                            Update For
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-700">
                            Effective Date
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-700">
                            Updated By
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-700">
                            Reason
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-700">
                            Remarks
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-700">
                            Last Update
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-700">
                            Attachments
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {historyRows.map((row, index) => {
                          const updateFor = getMovementUpdateForMeta(
                            row.update_for
                          );
                          const attachments = Array.isArray(row.attachments)
                            ? row.attachments
                            : [];

                          return (
                            <tr key={`${row.tbl_id || "movement"}-${index}`}>
                              <td className="px-3 py-2 text-slate-700">
                                <Badge color={updateFor.color}>
                                  {updateFor.label}
                                </Badge>
                              </td>
                              <td className="px-3 py-2 text-slate-700">
                                {formatMovementDate(row.effective_date)}
                              </td>
                              <td className="px-3 py-2 text-slate-700">
                                {row.updated_by || "N/A"}
                              </td>
                              <td className="px-3 py-2 text-slate-700">
                                {row.reason || "N/A"}
                              </td>
                              <td className="max-w-[460px] px-3 py-2 text-slate-700">
                                <span className="block whitespace-pre-wrap break-words">
                                  {row.remarks || "N/A"}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-slate-700">
                                {formatMovementDate(row.lastupdate_date, true)}
                              </td>
                              <td className="min-w-[280px] px-3 py-2 text-slate-700">
                                {attachments.length > 0 ? (
                                  <div className="space-y-2">
                                    {attachments.map((attachment) => {
                                      const attachmentName =
                                        getHistoryAttachmentFileName(attachment);
                                      const fileSize =
                                        formatAttachmentFileSize(
                                          attachment.file_size
                                        );
                                      const expired =
                                        Boolean(attachment.is_url_expired) ||
                                        isAttachmentUrlExpired(
                                          attachment.url_expires_at
                                        );
                                      const isRefreshing =
                                        historyRefreshingAttachmentId ===
                                        attachment.upload_id;

                                      return (
                                        <div
                                          key={attachment.upload_id}
                                          className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2"
                                        >
                                          <div className="flex items-start gap-2">
                                            <PaperClipIcon className="mt-0.5 h-4 w-4 flex-none text-slate-500" />
                                            <div className="min-w-0 flex-1">
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  handleOpenHistoryAttachment(
                                                    attachment
                                                  )
                                                }
                                                disabled={isRefreshing}
                                                title={attachmentName}
                                                className="block max-w-[230px] truncate text-left text-xs font-semibold text-blue-700 hover:text-blue-900 hover:underline disabled:cursor-not-allowed disabled:opacity-60"
                                              >
                                                {attachmentName}
                                              </button>
                                              <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                                                {fileSize ? (
                                                  <span>{fileSize}</span>
                                                ) : null}
                                                {expired ? (
                                                  <span className="rounded-full bg-amber-100 px-1.5 py-0.5 font-semibold text-amber-700">
                                                    Expired
                                                  </span>
                                                ) : null}
                                              </div>
                                            </div>
                                          </div>
                                          <div className="mt-2 flex flex-wrap items-center gap-2">
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleOpenHistoryAttachment(
                                                  attachment
                                                )
                                              }
                                              disabled={isRefreshing}
                                              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                                            >
                                              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                                              {isRefreshing
                                                ? "Refreshing..."
                                                : expired
                                                ? "Refresh & Open"
                                                : "Open"}
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleRefreshHistoryAttachment(
                                                  attachment
                                                )
                                              }
                                              disabled={isRefreshing}
                                              className="inline-flex items-center gap-1 rounded-md border border-blue-300 bg-blue-50 px-2 py-1 text-[11px] font-medium text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
                                            >
                                              <ArrowPathIcon
                                                className={clsx(
                                                  "h-3.5 w-3.5",
                                                  isRefreshing && "animate-spin"
                                                )}
                                              />
                                              Refresh Link
                                            </button>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <span className="text-xs text-slate-500">
                                    No attachments
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="border-t border-slate-200 bg-slate-50 px-5 py-3">
                <div className="flex flex-col items-center justify-between gap-3 text-sm text-slate-600 sm:flex-row">
                  <div>
                    Showing{" "}
                    {historyTotalCount > 0
                      ? (historyCurrentPage - 1) * historyItemsPerPage + 1
                      : 0}{" "}
                    to{" "}
                    {Math.min(
                      historyCurrentPage * historyItemsPerPage,
                      historyTotalCount
                    )}{" "}
                    of {historyTotalCount.toLocaleString()} entries
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => handleHistoryPageChange(1)}
                      disabled={historyCurrentPage === 1 || historyLoading}
                      className={clsx(
                        "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                        historyCurrentPage === 1
                          ? "bg-gray-100 text-gray-400"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      )}
                    >
                      <ChevronDoubleLeftIcon className="h-4 w-4" />
                      First
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        handleHistoryPageChange(historyCurrentPage - 1)
                      }
                      disabled={historyCurrentPage === 1 || historyLoading}
                      className={clsx(
                        "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                        historyCurrentPage === 1
                          ? "bg-gray-100 text-gray-400"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      )}
                    >
                      <ChevronLeftIcon className="h-4 w-4" />
                      Previous
                    </button>
                    {getHistoryPageNumbers().map((page) => (
                      <button
                        key={page}
                        type="button"
                        onClick={() => handleHistoryPageChange(page)}
                        className={clsx(
                          "rounded-md px-2 py-1 text-xs",
                          historyCurrentPage === page
                            ? "bg-blue-600 text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        )}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() =>
                        handleHistoryPageChange(historyCurrentPage + 1)
                      }
                      disabled={
                        historyCurrentPage === historyTotalPages ||
                        historyLoading
                      }
                      className={clsx(
                        "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                        historyCurrentPage === historyTotalPages
                          ? "bg-gray-100 text-gray-400"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      )}
                    >
                      Next
                      <ChevronRightIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleHistoryPageChange(historyTotalPages)}
                      disabled={
                        historyCurrentPage === historyTotalPages ||
                        historyLoading
                      }
                      className={clsx(
                        "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                        historyCurrentPage === historyTotalPages
                          ? "bg-gray-100 text-gray-400"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      )}
                    >
                      Last
                      <ChevronDoubleRightIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* View Modal */}
      <AnimatePresence>
        {viewCorpCode && (
          <CorporateViewModal
            corpCode={viewCorpCode}
            onClose={() => setViewCorpCode(null)}
          />
        )}
      </AnimatePresence>

      
    </div>
  );
};

export default CorporateTable;
